/**
 * 配置内容的多语言（给玩家看的），与后台界面语言（lib/i18n.tsx）是两回事。
 *
 * **en 是基准语言、发布必填**，其余语言留空时客户端回退到它。基准语言单独拎出来
 * 而不是「谁有值用谁」：回退链一旦不确定，同一个对象在不同端能显示成不同名字。
 *
 * 目录列 20 种、默认只展开 6 种：全铺开一个名字要填 20 行，绝大多数对象只发几个区。
 * 需要哪种在界面上现加，加过的语言会跟着数据存下来。
 */
export interface LocaleDef {
  id: string;
  label: string;
  /** 表格里的窄列标识 */
  short: string;
}

export const BASE_LOCALE = "en";

export const LOCALE_CATALOG: LocaleDef[] = [
  { id: "en", label: "English", short: "EN" },
  { id: "zh-CN", label: "简体中文", short: "简中" },
  { id: "zh-TW", label: "繁體中文", short: "繁中" },
  { id: "ja", label: "日本語", short: "日" },
  { id: "ko", label: "한국어", short: "한" },
  { id: "th", label: "ไทย", short: "TH" },
  { id: "vi", label: "Tiếng Việt", short: "VI" },
  { id: "id", label: "Bahasa Indonesia", short: "ID" },
  { id: "ms", label: "Bahasa Melayu", short: "MS" },
  { id: "fr", label: "Français", short: "FR" },
  { id: "de", label: "Deutsch", short: "DE" },
  { id: "es", label: "Español", short: "ES" },
  { id: "pt-BR", label: "Português (BR)", short: "PT" },
  { id: "it", label: "Italiano", short: "IT" },
  { id: "ru", label: "Русский", short: "RU" },
  { id: "tr", label: "Türkçe", short: "TR" },
  { id: "ar", label: "العربية", short: "AR" },
  { id: "hi", label: "हिन्दी", short: "HI" },
  { id: "nl", label: "Nederlands", short: "NL" },
  { id: "pl", label: "Polski", short: "PL" },
];

/** 默认展开的 = 客户端语言菜单里的五种（2026-10-09：简中 / 繁中 / English / 日本語 / Tiếng Việt）。缺的语言客户端拿到英文 */
export const DEFAULT_LOCALES = ["en", "zh-CN", "zh-TW", "ja", "vi"];

export const localeOf = (id: string): LocaleDef =>
  LOCALE_CATALOG.find((l) => l.id === id) ?? { id, label: id, short: id.toUpperCase() };

/** 语言 id → 文案。基准语言也存在这里，不再散落在对象顶层字段上 */
export type I18nText = Record<string, string>;

/** 取展示名：优先指定语言，回退基准语言，再回退任意有值的 */
export function pickText(v: I18nText | undefined, prefer: string = BASE_LOCALE): string {
  if (!v) return "";
  return v[prefer] || v[BASE_LOCALE] || Object.values(v).find(Boolean) || "";
}

/**
 * 已展开但还没填的语言。
 * 只看「已展开」的那几种——没加进来的 14 种语言本来就不打算发，
 * 把它们算成缺失会让每个对象永远顶着一条红字。
 */
export function missingLocales(v: I18nText | undefined, locales: string[] = DEFAULT_LOCALES): string[] {
  return locales.filter((id) => !v?.[id]?.trim());
}

/** 在给定的几种语言里填了几种（空白不算）：多语言按钮写「已填 3/4」用 */
export function filledOf(v: I18nText | undefined, locales: readonly string[]): { filled: number; total: number } {
  return { filled: locales.length - missingLocales(v, [...locales]).length, total: locales.length };
}

/** 数据里出现过的语言 ∪ 默认语言，按目录顺序排；这是编辑器要显示的行 */
export function localesOf(...values: (I18nText | undefined)[]): string[] {
  const set = new Set(DEFAULT_LOCALES);
  for (const v of values) for (const k of Object.keys(v ?? {})) set.add(k);
  const order = new Map(LOCALE_CATALOG.map((l, i) => [l.id, i]));
  return [...set].sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
}
